/**
 * Vista elegida por el usuario para cada listado.
 *
 * Cada listado declara en su código cuál es su vista por defecto (es una
 * decisión de diseño: los racks se miran mejor en lista, las ubicaciones en
 * árbol). Lo que el usuario cambia se guarda **en el servidor, en su usuario**
 * (`GET/PUT /api/ui-preferences/views`), para que le acompañe en cualquier
 * navegador. Aquí queda solo la caché del navegador, que sirve para pintar la
 * vista correcta al instante mientras llega la respuesta del servidor.
 *
 * `null` significa "sin elección": se usa la vista por defecto del listado. Por
 * eso volver a la vista por defecto BORRA la clave en vez de guardarla.
 */

export const VIEWS_STORAGE_KEY = 'cmdb-list-views';

/** Deben coincidir con `sanitize_view` del backend (`models/ui_preference.py`). */
export const KNOWN_VIEWS = ['list', 'card', 'tree', 'kanban'];

/**
 * Clave de vista aceptable. NO se limita a las cuatro históricas: un listado
 * puede declarar sus propias vistas (`models`/`manufacturers` en el catálogo,
 * `rules`/`counters` en nomenclatura) y el catálogo cerrado las descartaba, así
 * que el conmutador parecía un botón muerto. `ListPage` pinta la tabla estándar
 * cuando la clave guardada ya no corresponde a una vista del listado, de modo
 * que una clave desconocida nunca deja la pantalla vacía.
 */
const VIEW_KEY_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;

export function isValidView(value) {
  return typeof value === 'string' && VIEW_KEY_RE.test(value);
}

/** Mapa guardado en este navegador (`{}` si no hay o no se puede leer). */
export function readStoredViews() {
  try {
    const raw = localStorage.getItem(VIEWS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const clean = {};
    Object.entries(parsed).forEach(([key, view]) => {
      if (key && isValidView(view)) clean[key] = view;
    });
    return clean;
  } catch {
    return {};
  }
}

export function cacheViews(map) {
  try {
    localStorage.setItem(VIEWS_STORAGE_KEY, JSON.stringify(map || {}));
  } catch {
    // Navegador con almacenamiento bloqueado: se pierde el arranque al instante.
  }
}

/**
 * Combina lo que dice el servidor con lo que había en el navegador.
 * El **servidor manda** (es la preferencia del usuario, esté donde esté).
 */
export function resolveViews(serverViews, localViews = {}) {
  const next = { ...(localViews || {}) };
  Object.entries(serverViews || {}).forEach(([key, view]) => {
    if (key && isValidView(view)) next[key] = view;
  });
  cacheViews(next);
  return next;
}

/**
 * Devuelve el mapa actualizado con la vista de un listado.
 * `view` nulo (o igual a la vista por defecto) borra la clave.
 */
export function applyViewChange(current, listKey, view) {
  const next = { ...(current || {}) };
  if (!listKey) return next;
  if (isValidView(view)) next[listKey] = view;
  else delete next[listKey];
  return next;
}
